import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  reportsApi,
  DailyCensus,
  BedOccupancy,
  DoctorProductivityItem,
  DoctorProductivityPatientItem,
  FunnelData,
} from "@/api/reports";
import { format } from "date-fns";
import { Users, Stethoscope, BedDouble, Loader2, Filter, UserPlus, UserMinus, Activity, ArrowRight } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useChainReportHospitalScope } from "@/hooks/useChainReportHospitalScope";
import { ReportHospitalFilter } from "@/components/reports/ReportHospitalFilter";
import { formatDate } from "@/utils/dateUtils";

function formatRevenue(n: number): string {
  return n.toFixed(2);
}

const REVENUE_KEYS = ["opdRevenue", "ipdRevenue", "labRevenue", "pharmaRevenue", "daycareRevenue", "totalRevenue"] as const;

function sumPatientRevenue(
  patients: DoctorProductivityPatientItem[],
  key: (typeof REVENUE_KEYS)[number]
): number {
  return patients.reduce((sum, p) => sum + Number(p[key] ?? 0), 0);
}

/** Show one IST date, or from–to when a category spans multiple payment/discharge days. */
function formatExpenseDateUnderAmount(from?: string | null, to?: string | null): string | null {
  if (!from && !to) return null;
  const start = from ? formatDate(from) : null;
  const end = to ? formatDate(to) : null;
  if (start && end && start !== end) return `${start} – ${end}`;
  return start || end;
}

function RevenueWithDateCell({
  amount,
  dateFrom,
  dateTo,
  className,
}: {
  amount: number;
  dateFrom?: string | null;
  dateTo?: string | null;
  className?: string;
}) {
  const dateLabel = amount > 0 ? formatExpenseDateUnderAmount(dateFrom, dateTo) : null;
  return (
    <TableCell className={`text-right ${className ?? ""}`.trim()}>
      <div>{formatRevenue(amount)}</div>
      {dateLabel ? <div className="text-xs text-muted-foreground font-normal">{dateLabel}</div> : null}
    </TableCell>
  );
}

export default function OperationalReportsPage() {
  const [censusDate, setCensusDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [occupancyDate, setOccupancyDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [prodFrom, setProdFrom] = useState(format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), "yyyy-MM-dd"));
  const [prodTo, setProdTo] = useState(format(new Date(), "yyyy-MM-dd"));
  const [funnelFrom, setFunnelFrom] = useState(format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), "yyyy-MM-dd"));
  const [funnelTo, setFunnelTo] = useState(format(new Date(), "yyyy-MM-dd"));
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorProductivityItem | null>(null);
  const {
    showHospitalFilter,
    hospitalOptions,
    selectedHospitalId,
    setSelectedHospitalId,
    reportHospitalId,
    isSuperAdmin,
  } = useChainReportHospitalScope({ defaultScope: "consolidated" });
  const reportParams = showHospitalFilter
    ? { hospitalId: reportHospitalId }
    : undefined;

  const { data: census, isLoading: censusLoading } = useQuery({
    queryKey: ["reports-census", censusDate, reportParams?.hospitalId],
    queryFn: async () => {
      const res = await reportsApi.getDailyCensus(censusDate, reportParams);
      return res.data?.data as DailyCensus;
    },
  });

  const { data: occupancy, isLoading: occupancyLoading } = useQuery({
    queryKey: ["reports-occupancy", occupancyDate, reportParams?.hospitalId],
    queryFn: async () => {
      const res = await reportsApi.getBedOccupancy(occupancyDate, reportParams);
      return res.data?.data as BedOccupancy;
    },
  });

  const { data: productivity, isLoading: prodLoading } = useQuery({
    queryKey: ["reports-productivity", prodFrom, prodTo, reportParams?.hospitalId],
    queryFn: async () => {
      const res = await reportsApi.getDoctorProductivity(prodFrom, prodTo, reportParams);
      return res.data?.data as { from: string; to: string; doctors: DoctorProductivityItem[] };
    },
  });

  const { data: doctorPatients, isLoading: doctorPatientsLoading } = useQuery({
    queryKey: ["reports-productivity-patients", selectedDoctor?.doctorId, prodFrom, prodTo, reportParams?.hospitalId],
    queryFn: async () => {
      if (!selectedDoctor) return null;
      const res = await reportsApi.getDoctorProductivityPatients(
        selectedDoctor.doctorId,
        prodFrom,
        prodTo,
        reportParams
      );
      return res.data?.data ?? null;
    },
    enabled: !!selectedDoctor,
  });

  const { data: funnelData, isLoading: funnelLoading } = useQuery({
    queryKey: ["reports-funnel", funnelFrom, funnelTo, reportParams?.hospitalId],
    queryFn: async () => {
      const res = await reportsApi.getFunnel(funnelFrom, funnelTo, reportParams);
      return res.data?.data as { from: string; to: string; funnel: FunnelData };
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">Operational reports</h1>
        {showHospitalFilter ? (
          <ReportHospitalFilter
            hospitals={hospitalOptions}
            value={selectedHospitalId}
            onChange={setSelectedHospitalId}
            isSuperAdmin={isSuperAdmin}
          />
        ) : null}
      </div>
      <Tabs defaultValue="census">
        <TabsList>
          <TabsTrigger value="census">Daily census</TabsTrigger>
          <TabsTrigger value="doctors">Doctor productivity</TabsTrigger>
          <TabsTrigger value="beds">Bed occupancy</TabsTrigger>
          <TabsTrigger value="funnel">Funnel analysis</TabsTrigger>
        </TabsList>
        <TabsContent value="census" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Daily census
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Label>Date</Label>
                  <Input type="date" value={censusDate} onChange={(e) => setCensusDate(e.target.value)} className="w-40" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {censusLoading ? (
                <div className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
              ) : census ? (
                <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
                  <div className="rounded-lg border p-4">
                    <p className="text-sm text-muted-foreground">OPD visits (diagnosed)</p>
                    <p className="text-2xl font-semibold">{census.opdVisits}</p>
                  </div>
                  <div className="rounded-lg border p-4">
                    <p className="text-sm text-muted-foreground">IPD admissions (same day)</p>
                    <p className="text-2xl font-semibold">{census.ipdAdmissionsToday}</p>
                  </div>
                  <div className="rounded-lg border p-4">
                    <p className="text-sm text-muted-foreground">IPD discharges (same day)</p>
                    <p className="text-2xl font-semibold">{census.ipdDischargesToday}</p>
                  </div>
                  <div className="rounded-lg border p-4">
                    <p className="text-sm text-muted-foreground">Current in-patients</p>
                    <p className="text-2xl font-semibold">{census.currentInPatients}</p>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="doctors" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-4 flex-wrap">
                <CardTitle className="flex items-center gap-2">
                  <Stethoscope className="h-5 w-5" />
                  Doctor productivity
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Label>From</Label>
                  <Input type="date" value={prodFrom} onChange={(e) => setProdFrom(e.target.value)} className="w-40" />
                </div>
                <div className="flex items-center gap-2">
                  <Label>To</Label>
                  <Input type="date" value={prodTo} onChange={(e) => setProdTo(e.target.value)} className="w-40" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {prodLoading ? (
                <div className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
              ) : (
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Doctor</TableHead>
                      <TableHead>Specialisation</TableHead>
                      <TableHead>Appointments</TableHead>
                      <TableHead className="text-right">OPD (₹)</TableHead>
                      <TableHead className="text-right">IPD (₹)</TableHead>
                      <TableHead className="text-right">Lab (₹)</TableHead>
                      <TableHead className="text-right">Pharma (₹)</TableHead>
                      <TableHead className="text-right">Daycare (₹)</TableHead>
                      <TableHead className="text-right">Total (₹)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(productivity?.doctors ?? []).map((d) => (
                      <TableRow key={d.doctorId}>
                        <TableCell>
                          <button
                            type="button"
                            onClick={() => setSelectedDoctor(d)}
                            className="text-left font-medium text-primary hover:underline"
                          >
                            {d.name}
                          </button>
                        </TableCell>
                        <TableCell>{d.specialisation ?? "-"}</TableCell>
                        <TableCell>{d.appointments}</TableCell>
                        <TableCell className="text-right">{formatRevenue(d.opdRevenue)}</TableCell>
                        <TableCell className="text-right">{formatRevenue(d.ipdRevenue)}</TableCell>
                        <TableCell className="text-right">{formatRevenue(d.labRevenue)}</TableCell>
                        <TableCell className="text-right">{formatRevenue(d.pharmaRevenue)}</TableCell>
                        <TableCell className="text-right">{formatRevenue(d.daycareRevenue)}</TableCell>
                        <TableCell className="text-right font-medium">{formatRevenue(d.totalRevenue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              )}
            </CardContent>
          </Card>

          <Dialog open={!!selectedDoctor} onOpenChange={(open) => !open && setSelectedDoctor(null)}>
            <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle>
                  {selectedDoctor?.name}
                  {selectedDoctor?.specialisation ? ` · ${selectedDoctor.specialisation}` : ""}
                </DialogTitle>
                <p className="text-sm text-muted-foreground">
                  Patient revenue from {prodFrom} to {prodTo}
                </p>
              </DialogHeader>
              {doctorPatientsLoading ? (
                <div className="flex items-center gap-2 py-8">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading patients…
                </div>
              ) : (
                <div className="overflow-auto flex-1">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Patient</TableHead>
                        <TableHead>UHID</TableHead>
                        <TableHead className="text-right">OPD (₹)</TableHead>
                        <TableHead className="text-right">IPD (₹)</TableHead>
                        <TableHead className="text-right">Lab (₹)</TableHead>
                        <TableHead className="text-right">Pharma (₹)</TableHead>
                        <TableHead className="text-right">Daycare (₹)</TableHead>
                        <TableHead className="text-right">Total (₹)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(doctorPatients?.patients ?? []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                            No patient revenue in this period.
                          </TableCell>
                        </TableRow>
                      ) : (
                        <>
                          {(doctorPatients?.patients ?? []).map((p) => (
                            <TableRow key={p.patientId}>
                              <TableCell>{p.patientName}</TableCell>
                              <TableCell>{p.uhid ?? "—"}</TableCell>
                              <RevenueWithDateCell
                                amount={p.opdRevenue}
                                dateFrom={p.opdDateFrom}
                                dateTo={p.opdDateTo}
                              />
                              <RevenueWithDateCell
                                amount={p.ipdRevenue}
                                dateFrom={p.ipdDateFrom}
                                dateTo={p.ipdDateTo}
                              />
                              <RevenueWithDateCell
                                amount={p.labRevenue}
                                dateFrom={p.labDateFrom}
                                dateTo={p.labDateTo}
                              />
                              <RevenueWithDateCell
                                amount={p.pharmaRevenue}
                                dateFrom={p.pharmaDateFrom}
                                dateTo={p.pharmaDateTo}
                              />
                              <RevenueWithDateCell
                                amount={p.daycareRevenue}
                                dateFrom={p.daycareDateFrom}
                                dateTo={p.daycareDateTo}
                              />
                              <TableCell className="text-right font-medium">{formatRevenue(p.totalRevenue)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-muted/50 font-medium">
                            <TableCell colSpan={2}>Total ({doctorPatients?.patients.length ?? 0} patients)</TableCell>
                            <TableCell className="text-right">
                              {formatRevenue(sumPatientRevenue(doctorPatients?.patients ?? [], "opdRevenue"))}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatRevenue(sumPatientRevenue(doctorPatients?.patients ?? [], "ipdRevenue"))}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatRevenue(sumPatientRevenue(doctorPatients?.patients ?? [], "labRevenue"))}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatRevenue(sumPatientRevenue(doctorPatients?.patients ?? [], "pharmaRevenue"))}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatRevenue(sumPatientRevenue(doctorPatients?.patients ?? [], "daycareRevenue"))}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatRevenue(sumPatientRevenue(doctorPatients?.patients ?? [], "totalRevenue"))}
                            </TableCell>
                          </TableRow>
                        </>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>
        <TabsContent value="beds" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <BedDouble className="h-5 w-5" />
                  Bed occupancy
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Label>As of date</Label>
                  <Input type="date" value={occupancyDate} onChange={(e) => setOccupancyDate(e.target.value)} className="w-40" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {occupancyLoading ? (
                <div className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
              ) : occupancy ? (
                <>
                  <div className="grid gap-4 grid-cols-2 md:grid-cols-4 mb-4">
                    <div className="rounded-lg border p-4">
                      <p className="text-sm text-muted-foreground">Total beds</p>
                      <p className="text-2xl font-semibold">{occupancy.summary.totalBeds}</p>
                    </div>
                    <div className="rounded-lg border p-4">
                      <p className="text-sm text-muted-foreground">Occupied</p>
                      <p className="text-2xl font-semibold">{occupancy.summary.totalOccupied}</p>
                    </div>
                    <div className="rounded-lg border p-4">
                      <p className="text-sm text-muted-foreground">Available</p>
                      <p className="text-2xl font-semibold">{occupancy.summary.totalAvailable}</p>
                    </div>
                    <div className="rounded-lg border p-4">
                      <p className="text-sm text-muted-foreground">Occupancy %</p>
                      <p className="text-2xl font-semibold">{occupancy.summary.occupancyPercent}%</p>
                    </div>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ward</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Occupied</TableHead>
                        <TableHead>Available</TableHead>
                        <TableHead>%</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {occupancy.wards.map((w) => (
                        <TableRow key={w.wardId}>
                          <TableCell>{w.wardName}</TableCell>
                          <TableCell>{w.type}</TableCell>
                          <TableCell>{w.totalBeds}</TableCell>
                          <TableCell>{w.occupied}</TableCell>
                          <TableCell>{w.available}</TableCell>
                          <TableCell>{w.occupancyPercent}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="funnel" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Full funnel analysis
                </CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <Label>From</Label>
                  <Input type="date" value={funnelFrom} onChange={(e) => setFunnelFrom(e.target.value)} className="w-40" />
                  <Label>To</Label>
                  <Input type="date" value={funnelTo} onChange={(e) => setFunnelTo(e.target.value)} className="w-40" />
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Lead → OPD → IPD conversion and Lead → Lost in the selected period.
              </p>
            </CardHeader>
            <CardContent>
              {funnelLoading ? (
                <div className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
              ) : funnelData?.funnel ? (
                <>
                  <div className="grid gap-4 grid-cols-2 md:grid-cols-4 lg:grid-cols-7 mb-6">
                    <div className="rounded-lg border p-4 bg-muted/40">
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Activity className="h-4 w-4" /> Leads (new)
                      </p>
                      <p className="text-2xl font-semibold">{funnelData.funnel.leadsNew}</p>
                    </div>
                    <div className="rounded-lg border p-4">
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <UserMinus className="h-4 w-4" /> Lead → Lost
                      </p>
                      <p className="text-2xl font-semibold">{funnelData.funnel.leadsLost}</p>
                    </div>
                    <div className="rounded-lg border p-4">
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <UserPlus className="h-4 w-4" /> Lead → OPD (converted)
                      </p>
                      <p className="text-2xl font-semibold">{funnelData.funnel.leadsConverted}</p>
                    </div>
                    <div className="rounded-lg border p-4">
                      <p className="text-sm text-muted-foreground">New patients</p>
                      <p className="text-2xl font-semibold">{funnelData.funnel.newPatients}</p>
                    </div>
                    <div className="rounded-lg border p-4">
                      <p className="text-sm text-muted-foreground">OPD visits</p>
                      <p className="text-2xl font-semibold">{funnelData.funnel.opdVisits}</p>
                    </div>
                    <div className="rounded-lg border p-4">
                      <p className="text-sm text-muted-foreground">OPD → IPD</p>
                      <p className="text-2xl font-semibold">{funnelData.funnel.opdToIpd}</p>
                    </div>
                    <div className="rounded-lg border p-4">
                      <p className="text-sm text-muted-foreground">IPD admissions</p>
                      <p className="text-2xl font-semibold">{funnelData.funnel.ipdAdmissions}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground border-t pt-4">
                    <span className="font-medium text-foreground">Funnel:</span>
                    <span>Leads (new)</span>
                    <ArrowRight className="h-4 w-4" />
                    <span>Lead → Lost</span>
                    <span>·</span>
                    <span>Lead → OPD (converted)</span>
                    <ArrowRight className="h-4 w-4" />
                    <span>New patients</span>
                    <ArrowRight className="h-4 w-4" />
                    <span>OPD visits</span>
                    <ArrowRight className="h-4 w-4" />
                    <span>OPD → IPD</span>
                    <ArrowRight className="h-4 w-4" />
                    <span>IPD admissions</span>
                  </div>
                </>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
